import { useState, useEffect } from 'react';
import useApi from '../../api';
import { conLog } from '../../utils/conLog';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Search, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatRelativeTime } from '../../utils/dateFormatter';

export default function SelectFiles({ agentId, setShowFiles, setFilesSelected }: { agentId: string, setShowFiles: any, setFilesSelected: any }) {
  const [isOpen, setIsOpen] = useState(true);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [associating, setAssociating] = useState(false);
  const { get, post } = useApi();

  function closeModal() {
    setIsOpen(false);
    setShowFiles(false);
  }

  function toggleFileSelection(fileId: string) {
    setSelectedFiles((prev) => {
      const updated = new Set(prev);
      if (updated.has(fileId)) {
        updated.delete(fileId);
      } else {
        updated.add(fileId);
      }
      return updated;
    });
  }

  function associateFilesWithAgent() {
    const selectedFilesArray = Array.from(selectedFiles);
    setAssociating(true);

    post(`/agents/${agentId}/files`, { file_ids: selectedFilesArray })
      .then((response) => {
        setFilesSelected(selectedFilesArray);
        setShowFiles(false);
        closeModal();
        toast.success('Files associated successfully', toastConfig);
        return response.data;
      })
      .catch((error) => {
        console.error('Error associating files:', error);
        toast.error('Error associating files', toastConfig);
      })
      .finally(() => setAssociating(false));
  }

  useEffect(() => {
    if (!isOpen) return;

    const url = agentId ? `/files/?exclude_agent_id=${agentId}` : '/files/';

    get(url)
      .then((response) => {
        conLog(response.data);
        setFiles(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching files:', error);
        setLoading(false);
      });
  }, [isOpen, agentId]);

  const filteredFiles = files.filter((file: any) =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="sm:max-w-lg overflow-hidden">
        <DialogHeader>
            <div>
              <DialogTitle>Add existing document</DialogTitle>
              <DialogDescription className="mt-0.5">
                Select documents from your global knowledge to associate with this agent.
              </DialogDescription>
            </div>
        </DialogHeader>

        <div className="space-y-3 min-w-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {selectedFiles.size > 0 && (
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-xs font-medium text-muted-foreground">
                {selectedFiles.size} selected
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6"
                onClick={() => setSelectedFiles(new Set())}
              >
                Clear
              </Button>
            </div>
          )}

          <div className="h-[320px] rounded-lg border overflow-hidden">
            <div className="h-full overflow-y-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin h-5 w-5 border-2 border-muted-foreground rounded-full border-t-transparent"></div>
                <p className="text-sm text-muted-foreground mt-3">Loading documents...</p>
              </div>
            ) : filteredFiles.length > 0 ? (
              <div className="divide-y">
                {filteredFiles.map((file: any) => (
                  <label
                    key={file.id}
                    htmlFor={`fl-${file.id}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors overflow-hidden"
                  >
                    <Checkbox
                      id={`fl-${file.id}`}
                      checked={selectedFiles.has(file.id)}
                      onCheckedChange={() => toggleFileSelection(file.id)}
                      className="shrink-0"
                    />
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="text-sm font-medium truncate">{file.name && file.name.length > 60 ? file.name.slice(0, 60) + '...' : file.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {file.characters > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {file.characters.toLocaleString()} chars
                          </span>
                        )}
                        {file.created_at && (
                          <span className="text-xs text-muted-foreground flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatRelativeTime(file.created_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-muted-foreground mt-3">
                  {searchTerm ? 'No matching documents' : 'No documents available'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {searchTerm ? 'Try a different search term.' : 'Upload a document first, then you can share it across agents.'}
                </p>
              </div>
            )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            disabled={selectedFiles.size === 0 || associating}
            onClick={associateFilesWithAgent}
          >
            {associating ? (
              <div className="inline-flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Adding...
              </div>
            ) : (
              `Add ${selectedFiles.size > 0 ? selectedFiles.size : ''} Document${selectedFiles.size !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
